/**
 * ContratsAnomalies — Détecteur d'anomalies statistiques.
 * Flag contrats dont le prix, la durée ou le préavis s'écartent >3× MAD
 * de la médiane du portefeuille (par contract_type).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, Loader2, TrendingUp, TrendingDown, ArrowRight, Info, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);

const FIELD_LABEL = {
  monthly_ht: "Prix mensuel HT",
  cancellation_notice_days: "Préavis",
  duree_mois: "Durée",
};

export default function ContratsAnomalies({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mult, setMult] = useState(3.0);

  const load = async (m = mult) => {
    setData(null);
    try {
      const r = await api.get(`/contracts/anomalies?mad_multiplier=${m}`);
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); setData({ anomalies: [] }); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return (
    <div className="p-8 text-center">
      <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="anomalies-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Détecteur d'anomalies
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-3xl">
            Statistique 100% déterministe : pour chaque type de contrat, on
            calcule médiane + MAD (Median Absolute Deviation). Les contrats
            dont un champ dévie de plus de <b>{mult}× MAD</b> sont flaggés
            pour intervention CFO précoce.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Sensibilité (MAD)</label>
          <select id="anomalies-mad-select" name="anomalies-mad-select"
                    value={mult} onChange={(e) => { setMult(+e.target.value); load(+e.target.value); }}
                    className="text-xs border rounded px-2 py-1"
                    data-testid="mad-select">
            <option value={2.0}>2× (large)</option>
            <option value={3.0}>3× (recommandé)</option>
            <option value={4.0}>4× (strict)</option>
          </select>
        </div>
      </div>

      {/* Portfolio stats */}
      {data.groups_stats && Object.keys(data.groups_stats).length > 0 && (
        <Card className="p-4">
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
            Stats portefeuille par type
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {Object.entries(data.groups_stats).map(([type, s]) => (
              <div key={type} className="text-xs p-2 rounded border border-slate-200 bg-slate-50/40">
                <div className="font-medium truncate">{type}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {s.count} contrat(s)
                </div>
                {s.median_monthly_ht != null && (
                  <div className="text-[10px] font-mono text-slate-700 mt-1">
                    Méd. {fmtEUR(s.median_monthly_ht)}/mois
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Anomalies list */}
      {data.anomalies?.length === 0 ? (
        <Card className="p-8 text-center">
          <ShieldAlert className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
          <div className="text-sm text-slate-700 font-medium">
            Aucune anomalie détectée
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Ton portefeuille est cohérent statistiquement avec le seuil actuel.
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.anomalies.map((a) => (
            <Card key={a.contract_id}
              className={cn("p-4 border hover:shadow-md transition",
                a.severity >= 60 ? "border-red-200 bg-red-50/30"
                : "border-amber-200 bg-amber-50/30")}
              data-testid={`anomaly-${a.contract_id}`}>
              <div className="flex items-start gap-3">
                <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                  a.severity >= 60 ? "bg-red-500" : "bg-amber-500")}>
                  <AlertTriangle className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <button onClick={() => navigate(`/app/contracts/${a.contract_id}`)}
                    className="text-left group">
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-700">
                      {a.title || a.party_name}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {a.party_name} · {a.contract_type} · Risque {a.risk_score}
                    </div>
                  </button>
                  <div className="mt-2 space-y-1">
                    {a.deviations.map((d, i) => (
                      <div key={i} className="text-xs flex items-start gap-2 p-1.5 rounded bg-white border border-slate-100">
                        {d.value > d.median
                          ? <TrendingUp className="h-3 w-3 text-red-600 mt-0.5 shrink-0" />
                          : <TrendingDown className="h-3 w-3 text-blue-600 mt-0.5 shrink-0" />}
                        <div className="flex-1">
                          <span className="font-medium">{FIELD_LABEL[d.field] || d.field}</span>
                          <span className="text-slate-500"> : </span>
                          <span className="font-mono">
                            {d.field.includes("monthly") ? fmtEUR(d.value) : d.value}
                          </span>
                          <span className="text-slate-400"> (médiane </span>
                          <span className="font-mono">
                            {d.field.includes("monthly") ? fmtEUR(d.median) : d.median}
                          </span>
                          <span className="text-slate-400">)</span>
                          <div className="text-[10px] text-slate-500">{d.note}</div>
                        </div>
                        <Badge variant="outline" className="text-[9px] tabular-nums">
                          z={d.z_score}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center shrink-0">
                  <div className={cn("text-xl font-bold tabular-nums",
                    a.severity >= 60 ? "text-red-700" : "text-amber-700")}>
                    {a.severity}
                  </div>
                  <div className="text-[9px] text-slate-500 uppercase">score</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="text-[11px] text-slate-500 flex items-start gap-2 border-t pt-3">
        <Info className="h-3 w-3 shrink-0 mt-0.5" />
        <span>
          MAD (Median Absolute Deviation) = seuil statistique robuste, non
          affecté par les outliers. Un score-z de 3× MAD correspond à ~99% de
          probabilité que la valeur soit une anomalie réelle. Réserve un moment
          calme pour renégocier ces contrats atypiques.
        </span>
      </div>
    </div>
  );
}
